import React from 'react';
import { Users, Building2, ShieldCheck, Network } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';

interface WorkforceKpiCardsProps {
  totalWorkforce: number;
  activeDepartments: number;
  leadershipRoles: number;
  reportingManagers: number;
}

const KPI_CONFIG = [
  {
    key: 'totalWorkforce',
    icon: Users,
    label: 'Total Workforce',
    tone: 'primary' as const,
    bg: '#F2EDFF',
    iconColor: '#6825F5',
    dotBg: '#6825F5',
    accentBg: 'rgba(104, 37, 245, 0.06)',
  },
  {
    key: 'activeDepartments',
    icon: Building2,
    label: 'Active Departments',
    tone: 'success' as const,
    bg: '#E8F8F0',
    iconColor: '#20B982',
    dotBg: '#20B982',
    accentBg: 'rgba(32, 185, 130, 0.06)',
  },
  {
    key: 'leadershipRoles',
    icon: ShieldCheck,
    label: 'Leadership Roles',
    tone: 'warning' as const,
    bg: '#FFF4E6',
    iconColor: '#FF9D24',
    dotBg: '#FF9D24',
    accentBg: 'rgba(255, 157, 36, 0.06)',
  },
  {
    key: 'reportingManagers',
    icon: Network,
    label: 'Reporting Managers',
    tone: 'info' as const,
    bg: '#E8F1FF',
    iconColor: '#3986FF',
    dotBg: '#3986FF',
    accentBg: 'rgba(57, 134, 255, 0.06)',
  },
];

export function WorkforceKpiCards({
  totalWorkforce,
  activeDepartments,
  leadershipRoles,
  reportingManagers,
}: WorkforceKpiCardsProps) {
  const values = { totalWorkforce, activeDepartments, leadershipRoles, reportingManagers };

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {KPI_CONFIG.map((config, index) => (
        <Card
          key={config.key}
          className="relative h-[95px] flex items-center p-4 overflow-hidden"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8F5',
            borderRadius: '12px',
            boxShadow: '0 1px 2px rgba(17, 24, 63, 0.04), 0 4px 12px rgba(17, 24, 63, 0.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: config.bg,
              flexShrink: 0,
            }}
          >
            <config.icon
              style={{ color: config.iconColor, width: '20px', height: '20px' }}
              aria-hidden="true"
            />
          </div>

          <div style={{ marginLeft: '12px', flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: '24px',
                fontWeight: '700',
                lineHeight: '1.2',
                color: '#11183F',
                margin: 0,
              }}
            >
              {values[config.key as keyof typeof values]}
            </p>
            <p
              style={{
                fontSize: '14px',
                fontWeight: '400',
                color: '#747C9C',
                margin: '2px 0 0 0',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {config.label}
            </p>
          </div>

          <div
            className="absolute bottom-0 right-0 pointer-events-none"
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '0 0 12px 0',
              background: `radial-gradient(circle at top left, ${config.accentBg} 0%, transparent 70%)`,
              opacity: 0.5,
            }}
          />
        </Card>
      ))}
    </div>
  );
}